// QR Kiosk v2 — §8.3 machine-filtered chat-history takeover. Fetches this machine's
// threads (server-side ?machine_id= + client-side double-check), date-buckets them
// (imported PURE bucketOfV3), offers "Nový chat" (one additive reducer, no
// pre-created thread — first send reconciles the id, v1 parity) and opens a thread
// by hydrating the session from server truth. Nested surface → §7 shared floating
// BackPill, label kioskBack "Späť" (the REAL §7 key, not the spec's assumed
// kioskV2Back). Read-only history (no rename/delete on kiosk). Tokens + glyphs only.
import React, { useCallback, useEffect, useState } from "react";
import { useAppDispatch } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import chatBotApi from "../../../shared/chatBot/ChatBotApi";
import { bucketOfV3, V3DateBucket } from "../../../customer/midas/components/v3/v3-utils/threadBuckets";
import type { ThreadListApiItem } from "../../../customer/midas/midasPageModels";
import { Machine } from "../../models/machinesModels";
import { Glyph, GD } from "../shared/glyphs";
import { usePressed } from "../shared/usePressed";
import { BackPill } from "../shared/BackPill";
import { ErrorCallout } from "../shared/ErrorCallout";
import { TAKEOVER_LIST_PAD } from "../shared/kioskNav";
import { midasSessionHydrate, midasSessionNew } from "../kioskV2Slice";
import { mapKioskThreadMessages, ensureUtcSuffix } from "./kioskThreadMapping";
import { HistoryBucketHeader, HistoryRow, HistorySkeleton, HistoryEmpty } from "./MidasHistoryBits";

const BUCKET_ORDER: V3DateBucket[] = ["today", "yesterday", "thisWeek", "lastWeek", "earlier"];
const BUCKET_KEY: Record<V3DateBucket, string> = {
  today: "kioskV2HistToday",
  yesterday: "kioskV2HistYesterday",
  thisWeek: "kioskV2HistThisWeek",
  lastWeek: "kioskV2HistLastWeek",
  earlier: "kioskV2HistEarlier",
};

export interface MidasHistoryTakeoverV2Props {
  machine: Machine;
  currentThreadId: string | null;
  onDone: () => void;
}

export function MidasHistoryTakeoverV2({ machine, currentThreadId, onDone }: MidasHistoryTakeoverV2Props) {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("public");
  const newPress = usePressed();
  const [threads, setThreads] = useState<ThreadListApiItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [openError, setOpenError] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    chatBotApi
      .getThreads(50, 0, machine.id)
      .then((res) => setThreads((res.threads || []).filter((t) => t.machine_id === machine.id)))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [machine.id]);
  useEffect(() => {
    load();
  }, [load]);

  const openThread = (id: string) => {
    if (openingId) return;
    setOpeningId(id);
    setOpenError(false);
    chatBotApi
      .getThread(id)
      .then((detail) => {
        dispatch(midasSessionHydrate({ machineId: machine.id, threadId: id, messages: mapKioskThreadMessages(detail) }));
        onDone();
      })
      .catch(() => {
        setOpenError(true);
        setOpeningId(null);
      });
  };

  const newChat = () => {
    dispatch(midasSessionNew(machine.id));
    onDone();
  };

  const serialSuffix = machine.serialNumber ? ` · ${translate("serialNumber")} ${machine.serialNumber}` : "";
  const msgsLabel = translate("kioskV2HistMsgs");

  const grouped: Partial<Record<V3DateBucket, ThreadListApiItem[]>> = {};
  for (const t of threads || []) {
    const b = bucketOfV3(ensureUtcSuffix(t.updated_at));
    (grouped[b] = grouped[b] || []).push(t);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* Top bar — no back arrow (the §7 pill is the single nested-surface back). */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          height: 54,
          padding: "0 14px",
          background: "var(--card)",
          borderBottom: "1px solid var(--border)",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: "var(--midas-soft)",
            color: "var(--midas)",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <Glyph d={GD.hist} size={20} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ink)" }}>{translate("kioskV2MidasHistoryTitle")}</div>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--faint)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {`${machine.model}${serialSuffix}`}
          </div>
        </div>
      </div>

      {/* "Nový chat" action */}
      <button
        type="button"
        {...newPress.pressHandlers}
        onClick={newChat}
        style={{
          margin: "12px 14px 4px",
          height: 52,
          borderRadius: 12,
          background: "var(--midas-soft)",
          border: "1px solid var(--midas-line)",
          color: "color-mix(in oklab, var(--midas) 72%, var(--ink))",
          fontFamily: "inherit",
          fontSize: 15,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          cursor: "pointer",
          flexShrink: 0,
          ...newPress.pressStyle,
        }}
      >
        <Glyph d={GD.plus} size={18} />
        {translate("kioskV2MidasNewChat")}
      </button>

      {/* Scroll region */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "4px 14px" }}>
        {openError && (
          <div style={{ marginBottom: 8 }}>
            <ErrorCallout title={translate("kioskV2MidasHistoryOpenFailed")} />
          </div>
        )}
        {loading && !threads ? (
          <HistorySkeleton />
        ) : error ? (
          <ErrorCallout
            title={translate("kioskV2MidasHistoryLoadFailed")}
            onRetry={load}
            retryLabel={translate("kioskV2Retry")}
          />
        ) : threads && threads.length === 0 ? (
          <HistoryEmpty text={translate("kioskV2MidasHistoryEmpty")} />
        ) : (
          BUCKET_ORDER.filter((b) => grouped[b]?.length).map((b) => (
            <div key={b}>
              <HistoryBucketHeader label={translate(BUCKET_KEY[b])} />
              {grouped[b]!.map((t) => (
                <HistoryRow
                  key={t.id}
                  item={t}
                  active={t.id === currentThreadId}
                  opening={openingId === t.id}
                  msgsLabel={msgsLabel}
                  onOpen={() => openThread(t.id)}
                />
              ))}
            </div>
          ))
        )}
        {/* In-flow clearance so the last card never strands under the fixed pill. */}
        <div aria-hidden="true" style={{ height: TAKEOVER_LIST_PAD }} />
      </div>

      <BackPill label={translate("kioskBack")} onBack={onDone} zIndex={42} />
    </div>
  );
}

export default MidasHistoryTakeoverV2;
